import { InventoryType } from '../model/inventory-type.model'
import { Security } from '../model/security.model'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import { TransactionMatch } from '../model/single-transaction/transaction-match'
import { TransactionTypeEnum } from '../model/transaction-type.model'
import {
  calculateProceeds,
  calculateProceedsNonRounded,
} from '../util/common-utils'

export class Calculators {
  static calculateProceedsWithAssumptions(
    price: number,
    accruedInterest: number,
    units: number,
    commission: number,
    originationFee: number,
    drawFee: number,
    exitFee: number,
    exitFeeReceivable: number,
    prepaymentPenaltyFee: number,
    prepaidInterest: number,
    interestDue: number,
    reserveTotal: number,
    factor: number,
    hplMode: boolean,
  ): number {
    return calculateProceeds(
      price,
      accruedInterest,
      units,
      factor,
      commission,
      originationFee,
      drawFee,
      exitFee,
      exitFeeReceivable,
      prepaymentPenaltyFee,
      prepaidInterest,
      interestDue,
      reserveTotal,
      1.0,
      1.0,
      true,
      hplMode,
    )
  }
  /**
   * name
   */
  static calculateProceeds(
    price: number,
    accruedInterest: number,
    units: number,
    commission: number,
    originationFee: number,
    drawFee: number,
    exitFee: number,
    exitFeeReceivable: number,
    prepaymentPenaltyFee: number,
    prepaidInterest: number,
    interestDue: number,
    reserveTotal: number,
    security: Security,
    transactionType: TransactionTypeEnum,
    factor: number,
    hplMode: boolean,
    inventoryType?: InventoryType | string,
  ): number {
    if (security.securityTypeData?.isFuture) price = 0 // price paid is 0 for futures contracts
    if (!inventoryType) {
      inventoryType = InventoryType.LONG
    }
    if (!this.isBankOrMortgageLoan(security)) {
      originationFee = 0
      drawFee = 0
      prepaymentPenaltyFee = 0
      exitFee = 0
      exitFeeReceivable = 0
      prepaidInterest = 0
      interestDue = 0
      reserveTotal = 0
    }
    let negate = true
    if (
      transactionType == TransactionTypeEnum.OPTION_EXERCISE &&
      security.securityTypeData
    )
      negate =
        ((inventoryType == InventoryType.LONG || inventoryType == 'LONG') &&
          security.securityTypeData.isCallOption) ||
        ((inventoryType == InventoryType.SHORT || inventoryType == 'SHORT') &&
          security.securityTypeData.isPutOption)

    return calculateProceeds(
      price,
      accruedInterest,
      units,
      factor,
      commission,
      originationFee,
      drawFee,
      transactionType == TransactionTypeEnum.BUY ? 0 : exitFee,
      exitFeeReceivable,
      prepaymentPenaltyFee,
      prepaidInterest,
      interestDue,
      reserveTotal,
      security.securityMasterData?.priceScalar,
      security.securityMasterData?.contractSize
        ? security.securityMasterData?.contractSize
        : 1.0,
      negate,
      hplMode,
    )
  }

  static calculateAccruedInterest(
    security: Security,
    accruedInterestAmount: number,
    units: number,
    factor: number,
    isAbsolute: boolean,
  ): number {
    if (!security.securityTypeData?.isBond) factor = 1.0

    if (
      factor == 0 ||
      security.securityMasterData.priceScalar == 0 ||
      units == 0
    )
      return 0.0

    return isAbsolute
      ? Math.abs(
          accruedInterestAmount /
            factor /
            units /
            security.securityMasterData.priceScalar,
        )
      : (accruedInterestAmount /
          factor /
          units /
          security.securityMasterData.priceScalar) *
          -1.0
  }

  static calculateAccruedAmount(
    security: Security,
    accruedInterest: number,
    units: number,
    factor: number,
  ): number {
    if (!security.securityTypeData?.isBond) factor = 1.0

    if (
      factor == 0 ||
      security.securityMasterData?.priceScalar == 0 ||
      units == 0
    )
      return 0.0
    return (
      accruedInterest *
      factor *
      units *
      security.securityMasterData?.priceScalar *
      -1.0
    )
  }

  static calculatePriceForProceedMatch(
    accruedInterest: number,
    units: number,
    factor: number,
    commission: number,
    security: Security,
    proceeds: number,
  ): number {
    return (
      (proceeds + commission) /
        (-1 *
          (security.securityMasterData.contractSize
            ? security.securityMasterData.contractSize
            : 1.0) *
          factor *
          units *
          security.securityMasterData.priceScalar) -
      accruedInterest
    )
  }

  static calculateCombinedFactor(security: Security | undefined) {
    if (!security) return 0.0
    if (security.securityTypeData?.isBond) {
      const indexFactor = security.securityIndexFactor?.factor ?? 0.0
      const principalFactor = security.securityPrincipalFactor?.factor ?? 0.0
      return principalFactor * indexFactor
    } else {
      return 1.0
    }
  }

  static calculateUnits(
    matchExternal: TransactionMatch | ExternalUnmatchedTransaction | undefined,
    security: Security,
    combinedFactor: number,
  ): number {
    const unitsCustody = matchExternal?.units ?? 0
    if (combinedFactor == 1 || !matchExternal) {
      return unitsCustody
    }
    const unitsByFactor = unitsCustody / combinedFactor
    const unitsByFactorRounded = Math.round(unitsCustody / combinedFactor)
    const unitsCandidates = [unitsCustody, unitsByFactorRounded, unitsByFactor]
    let bestGuess = unitsCustody
    let difference = Number.MAX_VALUE

    unitsCandidates.forEach(unitsCandidate => {
      const decimalAccrued = Calculators.calculateAccruedInterest(
        security,
        matchExternal.accruedAmount ?? 0,
        unitsCandidate,
        combinedFactor,
        true,
      )
      const proceeds = calculateProceedsNonRounded(
        matchExternal.price ?? 0,
        decimalAccrued,
        unitsCandidate,
        combinedFactor,
        matchExternal.commission ?? 0,
        matchExternal.originationFee ?? 0,
        matchExternal.drawFee ?? 0,
        matchExternal.exitFee ?? 0,
        matchExternal.exitFeeReceivable ?? 0,
        matchExternal.prepaymentPenaltyFee ?? 0,
        matchExternal.prepaidInterest ?? 0,
        matchExternal.interestDue ?? 0,
        (matchExternal.interestBearingInterestReserve ?? 0) +
          (matchExternal.interestBearingPrincipalReserve ?? 0) +
          (matchExternal.nonInterestBearingInterestReserve ?? 0) +
          (matchExternal.nonInterestBearingPrincipalReserve ?? 0),
        security.securityMasterData?.priceScalar,
        security.securityMasterData?.contractSize ?? 1.0,
        -1,
      )
      if (proceeds != 0) {
        const candidateDifference = Math.abs(matchExternal.amount - proceeds)
        if (candidateDifference < difference) {
          difference = candidateDifference
          bestGuess = unitsCandidate
        }
      }
    })
    return bestGuess
  }

  static isBankOrMortgageLoan = (security: Security | undefined) => {
    const type =
      security?.securityType ?? security?.securityMasterData?.typeShortName
    return type == 'MORTGAGE LOAN' || type == 'BANK LOAN'
  }
}
